import { StateCreator } from "zustand";

import { isEmbedded, isExternalEmbed } from "@shared/utils/window/window-utils";

import { DataProvider } from "@shared/constants/common";
import {
  BASKET_ID_STORAGE_KEYS,
  BASKET_STORAGE_KEYS,
  WIDGET_BASKET_COMMANDS,
} from "@shared/constants/post-messages";
import { SITE_URL } from "@shared/env";

import { getPlatform } from "@shared/utils/get-platform";
import {
  set as setInCacheStorage,
  parseFromLocalStorage,
} from "@shared/utils/storage/cache-storage";
import { TicketBasketType } from "@shared/modules/payments/types";
import { type Store } from "../useStore";

export type BasketSlice = {
  isBasketUpdating: boolean;
  basketError: unknown;
  publicBasket?: TicketBasketType;
  venueBasket?: TicketBasketType;
  getBasket(type: DataProvider): TicketBasketType | undefined;
  setBasket(type: DataProvider, basket?: TicketBasketType): void;
  setIsBasketUpdating(isUpdating: boolean): void;
  setBasketError: (error: unknown) => void;
};

const getBasketStorageKey = () => {
  if (isEmbedded() && isExternalEmbed()) {
    return BASKET_STORAGE_KEYS.WIDGET.id;
  }
  return BASKET_STORAGE_KEYS.WEB.id;
};

const getBasketIdStorageKey = () => {
  if (isEmbedded() && isExternalEmbed()) {
    return BASKET_ID_STORAGE_KEYS.WIDGET.id;
  }
  return BASKET_ID_STORAGE_KEYS.WEB.id;
};

export const createBasketSlice: StateCreator<Store, [], [], BasketSlice> = (
  set,
  get
) => ({
  publicBasket: undefined,
  venueBasket: undefined,
  isBasketUpdating: false,
  basketError: undefined,

  getBasket(type: DataProvider): TicketBasketType | undefined {
    switch (type) {
      case DataProvider.User:
      case DataProvider.Public: {
        const basketStorageKey = getBasketStorageKey();

        // get angular basket from local storage if it exists
        const basket = parseFromLocalStorage<{ $value: TicketBasketType }>(
          basketStorageKey
        )?.$value;
        if (basket?.id) {
          return basket;
        }

        return get().publicBasket;
      }
      case DataProvider.Venue: {
        return get().venueBasket;
      }
      default: {
        throw new Error("This basket type is not supported");
      }
    }
  },

  setBasket(type: DataProvider, basket?: TicketBasketType) {
    // send message to update widget cart in AngularJS
    if (getPlatform() !== "MOBILE" && isEmbedded()) {
      window.parent.postMessage(
        WIDGET_BASKET_COMMANDS.UPDATE_WIDGET_CART.id,
        SITE_URL
      );
    }

    switch (type) {
      case DataProvider.User:
      case DataProvider.Public: {
        const basketStorageKey = getBasketStorageKey();
        const basketIdStorageKey = getBasketIdStorageKey();

        // set basket details in local storage under the storageKey value so that angular can access it
        setInCacheStorage(
          basketStorageKey,
          basket,
          basket?.expiry_date ? new Date(basket.expiry_date) : null
        );
        setInCacheStorage(
          basketIdStorageKey,
          basket?.id,
          basket?.expiry_date ? new Date(basket.expiry_date) : null
        );

        set({ publicBasket: basket });
        break;
      }
      case DataProvider.Venue: {
        set({ venueBasket: basket });
        break;
      }
      default: {
        throw new Error("This basket type is not supported");
      }
    }
  },

  setIsBasketUpdating(isUpdating: boolean) {
    return set({ isBasketUpdating: isUpdating });
  },

  setBasketError(error: unknown) {
    return set({ basketError: error });
  },
});
